import { memo, useEffect, useRef } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';

import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { NOTIFICATIONS_INFO, SLACK_CHANNEL_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import { NotificationService, ProjectNotificationValue } from '../types';
import { notificationInputErrors } from '../validation';

const SLACK_CHANNEL_MAX_LENGTH = 63;

const NOTIFICATION_SERVICES: { service: NotificationService; label: string }[] =
  [
    { service: 'slack', label: 'Slack' },
    { service: 'grafana', label: 'Grafana' },
    { service: 'github', label: 'GitHub' },
  ];

export interface NotificationSectionProps {
  value: ProjectNotificationValue;
  onChange: (value: ProjectNotificationValue) => void;
  errors?: string[];
}

const NotificationSectionInner = ({
  value,
  onChange,
  errors = [],
}: NotificationSectionProps) => {
  const { classes } = useStyles();

  const channelError =
    errors.length > 0
      ? notificationInputErrors(
          value.notificationChannel,
          value.notificationServices,
        )
      : '';
  const hasChannelError = channelError !== '';

  const channelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasChannelError) {
      channelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [hasChannelError]);

  const selectedServices = value.notificationServices || [];
  const slackSelected = selectedServices.includes('slack');

  const handleServiceToggle = (service: NotificationService) => {
    const updated = selectedServices.includes(service)
      ? selectedServices.filter(s => s !== service)
      : [...selectedServices, service];
    onChange({ ...value, notificationServices: updated });
  };

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Notifications"
          description="Configure notification settings"
          variant="h5"
          divider
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Notification services"
            description="Select the services that should receive notifications for this project."
            infoDoc={NOTIFICATIONS_INFO}
          />
          <FormGroup>
            {NOTIFICATION_SERVICES.map(({ service, label }) => (
              <FormControlLabel
                key={service}
                control={
                  <Checkbox
                    checked={selectedServices.includes(service)}
										onChange={() => handleServiceToggle(service)}
                    color="secondary"
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
        </Box>
        <div ref={channelRef} className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Slack notifications channel"
            description="Configure Slack channel where notifications will be sent for this project"
            infoDoc={SLACK_CHANNEL_INFO}
          />
          <TextField
            id="notification-slack-channel"
            label="Slack Channel"
            value={value.notificationChannel || ''}
            onChange={e =>
              onChange({ ...value, notificationChannel: e.target.value })
            }
            variant="outlined"
            size="small"
            fullWidth
            required={slackSelected}
            error={hasChannelError}
            disabled={!slackSelected}
            slotProps={{
              htmlInput: { maxLength: SLACK_CHANNEL_MAX_LENGTH },
            }}
            helperText={
              hasChannelError ? (
                <ScaffolderFieldError message={channelError} />
              ) : (
                'Up to 63 characters. Allowed: lowercase letters, numbers, hyphens, underscores.'
              )
            }
          />
        </div>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const NotificationSection = memo(NotificationSectionInner);
